import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, App, Button, Card, Form, Input, Modal, Popconfirm, Segmented, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, HolderOutlined, SwapOutlined, MergeOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ChangeFieldTypeResult, FieldDefinitionDto, FieldTypeExample, FieldType, MergeFieldInput, MergeFieldResult, ObjectType } from '@crm/shared';
import { FIELD_TYPES, MERGEABLE_SYSTEM_KEYS } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useChangeFieldType, useCreateField, useDeleteField, useFieldTypePreview, useFieldUsage, useFieldUsageCounts, useFields, useMergeField, useReorderFields, useUpdateField } from '../../features/fields/api';
import { plural } from '../../utils/format';
import { canQuickAdd } from '../../features/fields/system-keys';
import { tableLocale, tableLoading } from '../../components/states';

const TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Text',
  TEXTAREA: 'Long text',
  NUMBER: 'Number',
  CURRENCY: 'Currency',
  DATE: 'Date',
  DATETIME: 'Date & time',
  BOOLEAN: 'Yes / No',
  SELECT: 'Select',
  MULTISELECT: 'Multi-select',
  EMAIL: 'Email',
  PHONE: 'Phone',
  URL: 'URL',
  LINKS: 'Links (with labels)',
  LIST: 'List',
};

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9]/, 'f$&')
    .slice(0, 64);

function SortableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props['data-row-key'] });
  const style: React.CSSProperties = { ...props.style, transform: CSS.Translate.toString(transform), transition, ...(isDragging ? { position: 'relative', zIndex: 10, background: '#fafafa' } : {}) };
  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
}

export function FieldsPage() {
  const [objectType, setObjectType] = useState<ObjectType>('CONTACT');
  return (
    <div className="page">
      <PageHeader title="Fields" subtitle="Define the fields on contacts, organizations and deals. Drag rows to reorder forms and tables." />
      <Card>
        <FieldsTable
          key={objectType}
          objectType={objectType}
          switcher={
            <Segmented
              value={objectType}
              onChange={(k) => setObjectType(k as ObjectType)}
              options={[
                { value: 'CONTACT', label: 'Contact' },
                { value: 'ORGANIZATION', label: 'Organization' },
                { value: 'DEAL', label: 'Deal' },
              ]}
            />
          }
        />
      </Card>
    </div>
  );
}

function FieldsTable({ objectType, switcher }: { objectType: ObjectType; switcher: ReactNode }) {
  const { message } = App.useApp();
  const fields = useFields(objectType);
  const usage = useFieldUsageCounts(objectType);
  const create = useCreateField();
  const update = useUpdateField();
  const remove = useDeleteField();
  const reorder = useReorderFields();
  const [editing, setEditing] = useState<FieldDefinitionDto | 'new' | null>(null);
  // remount the modal on every open: its form store otherwise outlives it, and antd merges initialValues *under*
  // what is left in the store, so the next field would open with the previous one's values
  const [editSeq, setEditSeq] = useState(0);
  const edit = (f: FieldDefinitionDto | 'new') => {
    setEditing(f);
    setEditSeq((n) => n + 1);
  };
  const [merging, setMerging] = useState<FieldDefinitionDto | 'pick' | null>(null);
  const [retyping, setRetyping] = useState<{ field: FieldDefinitionDto; type?: FieldType } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // contact mobile numbers live in the Phones list (label "Mobile"), so the mobile column is shown under Phone
  const mobile = objectType === 'CONTACT' ? fields.data?.find((f) => f.isSystem && f.key === 'mobile') : undefined;
  const rows = useMemo(() => (fields.data ?? []).filter((f) => f !== mobile), [fields.data, mobile]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((r) => r.id === active.id);
    const to = rows.findIndex((r) => r.id === over.id);
    const next = arrayMove(rows, from, to);
    try {
      await reorder.mutateAsync({ objectType, ids: next.flatMap((r) => (r.key === 'phone' && mobile ? [r.id, mobile.id] : [r.id])) });
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const toggle = async (row: FieldDefinitionDto, patch: { showInTable?: boolean; required?: boolean; moreInfo?: boolean; quickAdd?: boolean }) => {
    try {
      await update.mutateAsync({ id: row.id, ...patch });
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <>
      <div className="table-toolbar">
        <div className="toolbar-group">{switcher}</div>
        <div className="toolbar-group">
          <Button icon={<MergeOutlined />} onClick={() => setMerging('pick')} disabled={!rows.some((r) => !r.isSystem)}>
            Merge fields
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => edit('new')}>
            Add field
          </Button>
        </div>
      </div>
      <DndContext sensors={sensors} onDragEnd={(e) => void onDragEnd(e)}>
        <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <Table<FieldDefinitionDto>
            rowKey="id"
            loading={tableLoading(fields)}
            locale={tableLocale(fields, 'No fields yet. Add one to start capturing data.')}
            dataSource={rows}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 1540 }}
            components={{ body: { row: SortableRow } }}
            columns={[
              { key: 'drag', width: 36, render: () => <HolderOutlined aria-label="Drag to reorder" style={{ cursor: 'grab', color: '#999' }} /> },
              {
                title: 'Label',
                dataIndex: 'label',
                width: 270,
                render: (v, r) => (
                  <Space size={6}>
                    <a onClick={() => edit(r)}>{v}</a>
                    {r.isSystem && <Tooltip title="Built-in field: the label can change, the field cannot be deleted"><Tag>System</Tag></Tooltip>}
                    {objectType === 'CONTACT' && r.isSystem && (r.key === 'email' || r.key === 'phone') && (
                      <Tooltip title={r.key === 'email' ? 'Every address of the contact, each with a label. Tables, filters and merge tags use the primary one.' : 'Every number of the contact, each with a label; mobile numbers are the ones labelled "Mobile". Tables, filters and merge tags use the primary one (and the first Mobile one as "Mobile").'}>
                        <Tag color="blue">{r.key === 'email' ? 'List' : 'List · Mobile'}</Tag>
                      </Tooltip>
                    )}
                  </Space>
                ),
              },
              { title: 'Key', dataIndex: 'key', width: 180, ellipsis: true, render: (v) => <Typography.Text code style={{ whiteSpace: 'nowrap' }}>{v}</Typography.Text> },
              { title: 'Type', dataIndex: 'type', width: 120, render: (t: FieldType) => TYPE_LABELS[t] },
              {
                title: <Tooltip title={`How many ${RECORD_NOUN[objectType]}s have a value in the field. Built-in yes/no fields count the ones set to yes.`}>In use</Tooltip>,
                key: 'usage',
                width: 110,
                render: (_, r) => {
                  const n = usage.data?.[r.id];
                  if (n === undefined) return usage.isLoading ? '…' : '—';
                  if (n === 0) return <Tooltip title={`No ${RECORD_NOUN[objectType]} has a value${r.isSystem ? '' : ': safe to delete'}`}><Tag color="warning">Empty</Tag></Tooltip>;
                  return n.toLocaleString();
                },
              },
              { title: 'Group', dataIndex: 'group', width: 120, ellipsis: true, render: (v) => v ?? '—' },
              {
                title: <Tooltip title="More information fields sit in a collapsed &quot;View more&quot; section at the bottom of profiles and forms">Section</Tooltip>,
                dataIndex: 'moreInfo',
                width: 150,
                render: (v: boolean, r) => (
                  <Segmented
                    size="small"
                    value={v ? 'more' : 'main'}
                    onChange={(k) => void toggle(r, { moreInfo: k === 'more' })}
                    options={[
                      { value: 'main', label: 'Main' },
                      { value: 'more', label: 'More info' },
                    ]}
                  />
                ),
              },
              {
                title: 'Options',
                dataIndex: 'options',
                width: 200,
                ellipsis: true,
                render: (o: { label: string }[] | null) => (o?.length ? <Typography.Text type="secondary">{o.map((x) => x.label).join(', ')}</Typography.Text> : '—'),
              },
              { title: 'Required', dataIndex: 'required', width: 90, render: (v, r) => <Switch size="small" checked={v} disabled={r.isSystem} onChange={(c) => void toggle(r, { required: c })} /> },
              { title: 'In table', dataIndex: 'showInTable', width: 90, render: (v, r) => <Switch size="small" checked={v} onChange={(c) => void toggle(r, { showInTable: c })} /> },
              {
                title: <Tooltip title={`Fields on the quick create modal, opened with the + button wherever a ${RECORD_NOUN[objectType]} can be picked. Required fields are always on it.`}>Quick add</Tooltip>,
                dataIndex: 'quickAdd',
                width: 100,
                render: (v: boolean, r) =>
                  !canQuickAdd(r) ? null : r.required ? (
                    <Tooltip title="Required fields are always on the quick create modal">
                      <Switch size="small" checked disabled />
                    </Tooltip>
                  ) : (
                    <Switch size="small" checked={v} aria-label={`Show ${r.label} on quick add`} onChange={(c) => void toggle(r, { quickAdd: c })} />
                  ),
              },
              {
                key: 'actions',
                width: 120,
                render: (_, r) =>
                  r.isSystem ? null : (
                    <Space size={0}>
                      <Button size="small" type="text" icon={<SwapOutlined />} aria-label={`Change type of ${r.label}`} title="Change type" onClick={() => setRetyping({ field: r })} />
                      <Button size="small" type="text" icon={<MergeOutlined />} aria-label={`Merge field ${r.label}`} title="Merge into another field" onClick={() => setMerging(r)} />
                      <Popconfirm title={`Delete field "${r.label}"? Stored values are kept but hidden.`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove.mutateAsync(r.id).then(() => message.success('Field deleted')).catch((e) => message.error(errorMessage(e)))}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Delete field ${r.label}`} title="Delete field" />
                      </Popconfirm>
                    </Space>
                  ),
              },
            ]}
          />
        </SortableContext>
      </DndContext>
      <MergeFieldModal objectType={objectType} fields={rows} source={merging} onClose={() => setMerging(null)} />
      <ChangeTypeModal key={retyping?.field.id ?? 'none'} objectType={objectType} field={retyping?.field ?? null} initialType={retyping?.type} onClose={() => setRetyping(null)} />
      <FieldModal
        key={editSeq}
        objectType={objectType}
        field={editing}
        saving={create.isPending || update.isPending}
        onClose={() => setEditing(null)}
        onChangeType={(f, type) => {
          setEditing(null);
          setRetyping({ field: f, type });
        }}
        onSave={async (values) => {
          try {
            if (editing === 'new') await create.mutateAsync({ objectType, ...values } as Parameters<typeof create.mutateAsync>[0]);
            else if (editing) await update.mutateAsync({ id: editing.id, ...values });
            message.success(editing === 'new' ? 'Field created' : 'Field saved');
            setEditing(null);
          } catch (e) {
            message.error(errorMessage(e));
          }
        }}
      />
    </>
  );
}

interface FieldFormValues {
  label: string;
  key: string;
  type: FieldType;
  group?: string;
  description?: string;
  required: boolean;
  showInTable: boolean;
  moreInfo: boolean;
  quickAdd: boolean;
  options?: { value: string; label: string }[];
}

function FieldModal({ objectType, field, saving, onClose, onChangeType, onSave }: { objectType: ObjectType; field: FieldDefinitionDto | 'new' | null; saving: boolean; onClose: () => void; onChangeType: (f: FieldDefinitionDto, type?: FieldType) => void; onSave: (v: FieldFormValues) => Promise<void> }) {
  const [form] = Form.useForm<FieldFormValues>();
  const isNew = field === 'new';
  const existing = field && field !== 'new' ? field : null;
  const type = Form.useWatch('type', form);
  const needsOptions = type === 'SELECT' || type === 'MULTISELECT';

  return (
    <Modal
      title={isNew ? `New ${objectType.toLowerCase()} field` : `Edit field: ${existing?.label ?? ''}`}
      open={!!field}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isNew ? 'Create' : 'Save'}
      confirmLoading={saving}
      destroyOnHidden
      width={560}
    >
      <Form<FieldFormValues>
        form={form}
        preserve={false}
        layout="vertical"
        requiredMark={false}
        initialValues={
          existing
            ? { label: existing.label, key: existing.key, type: existing.type, group: existing.group ?? undefined, description: existing.description ?? undefined, required: existing.required, showInTable: existing.showInTable, moreInfo: existing.moreInfo, quickAdd: existing.quickAdd, options: existing.options ?? [] }
            : { type: 'TEXT', required: false, showInTable: false, moreInfo: false, quickAdd: false, options: [] }
        }
        onFinish={(v) => void onSave({ ...v, options: needsOptions ? v.options : undefined })}
        onValuesChange={(changed, all) => {
          if (isNew && changed.label !== undefined && !all.key?.length) form.setFieldValue('key', slug(changed.label));
          if (isNew && changed.label !== undefined && all.key === slug(all.label.slice(0, -1))) form.setFieldValue('key', slug(changed.label));
        }}
      >
        <Form.Item name="label" label="Label" rules={[{ required: true }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="key" label="Key" rules={[{ required: true, pattern: /^[a-z][a-z0-9_]{0,63}$/, message: 'lowercase letters, numbers and underscores' }]} extra="Used in filters, exports and merge tags. Cannot change later.">
          <Input disabled={!isNew} />
        </Form.Item>
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true }]}
          extra={existing && !existing.isSystem ? 'Changing the type converts the stored values; you see a preview first.' : undefined}
        >
          {/* an existing field's type changes through the conversion preview, not by saving this form */}
          <Select
            disabled={!!existing?.isSystem}
            options={FIELD_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] }))}
            onChange={(t: FieldType) => {
              if (!existing || t === existing.type) return;
              form.setFieldValue('type', existing.type);
              onChangeType(existing, t);
            }}
          />
        </Form.Item>
        {needsOptions && (
          <Form.List name="options">
            {(items, { add, remove }) => (
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong>Options</Typography.Text>
                {items.map((it) => (
                  <Space key={it.key} align="baseline" style={{ display: 'flex', marginTop: 8 }}>
                    <Form.Item name={[it.name, 'label']} rules={[{ required: true, message: 'Label' }]} noStyle>
                      <Input placeholder="Label" onChange={(e) => { const opts = form.getFieldValue('options') as { value?: string }[]; if (!opts[it.name]?.value) form.setFieldValue(['options', it.name, 'value'], slug(e.target.value)); }} />
                    </Form.Item>
                    <Form.Item name={[it.name, 'value']} rules={[{ required: true, message: 'Value' }]} noStyle>
                      <Input placeholder="value" />
                    </Form.Item>
                    <Button type="text" size="small" icon={<MinusCircleOutlined />} aria-label="Remove option" title="Remove option" onClick={() => remove(it.name)} />
                  </Space>
                ))}
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ label: '', value: '' })} style={{ marginTop: 8 }}>
                  Add option
                </Button>
              </div>
            )}
          </Form.List>
        )}
        <Form.Item name="group" label="Group" extra="Fields are grouped under this heading on forms and profiles.">
          <Input placeholder="e.g. Address" />
        </Form.Item>
        <Form.Item name="moreInfo" label="Section" getValueProps={(v: boolean) => ({ value: v ? 'more' : 'main' })} normalize={(v: string) => v === 'more'} extra="More information fields are tucked into a collapsed &quot;View more&quot; section at the bottom of profiles and forms.">
          <Segmented
            options={[
              { value: 'main', label: 'Main information' },
              { value: 'more', label: 'More information' },
            ]}
          />
        </Form.Item>
        <Form.Item name="description" label="Help text">
          <Input />
        </Form.Item>
        <Space size="large">
          <Form.Item name="required" label="Required" valuePropName="checked">
            <Switch disabled={!!existing?.isSystem} />
          </Form.Item>
          <Form.Item name="showInTable" label="Show in table by default" valuePropName="checked">
            <Switch />
          </Form.Item>
          {(!existing || canQuickAdd(existing)) && (
            <Form.Item name="quickAdd" label="Show on quick add" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Space>
      </Form>
    </Modal>
  );
}

const RECORD_NOUN: Record<ObjectType, string> = { CONTACT: 'contact', ORGANIZATION: 'organization', DEAL: 'deal' };


/** How stored values become the new type, in a sentence. */
const CONVERSION_NOTES: Record<FieldType, string> = {
  TEXT: 'Lists and multi-selects are written out one value after another, joined by the separator below; options become their labels.',
  TEXTAREA: 'Lists and multi-selects are written out one value after another, joined by the separator below; options become their labels.',
  NUMBER: 'Text is read as a number (spaces, thousands separators and a currency sign are ignored); yes/no becomes 1/0.',
  CURRENCY: 'Text is read as an amount (spaces, thousands separators and a currency sign are ignored); yes/no becomes 1/0.',
  DATE: 'Text that reads as a date becomes one. A time of day cannot be kept, so values with one also keep their original.',
  DATETIME: 'Text that reads as a date becomes one; dates become midnight UTC.',
  BOOLEAN: 'Yes / no, true / false, y / n, on / off and 1 / 0 become yes or no.',
  SELECT: 'Each distinct value becomes an option. A select holds one value, so lists keep their first value (and their original).',
  MULTISELECT: 'Each distinct value becomes an option; short text is split on commas.',
  EMAIL: 'Values that are email addresses are kept (lowercased).',
  PHONE: 'Values with a number in them are kept as they are.',
  URL: 'Values that are web addresses are kept. Link labels and extra links cannot be kept, so those values also keep their original.',
  LINKS: 'Web addresses become links; several separated by spaces or commas become separate links.',
  LIST: 'Each value becomes a list item (options become their labels); short text is split on commas.',
};

/** Field types that hold several values, which text joins with a separator. */
const MULTI_VALUE_TYPES = new Set<FieldType>(['MULTISELECT', 'LIST', 'LINKS']);
const SEPARATORS = [
  { value: ' ', label: 'Space' },
  { value: ', ', label: 'Comma  ( , )' },
  { value: '; ', label: 'Semicolon  ( ; )' },
  { value: ' / ', label: 'Slash  ( / )' },
  { value: ' | ', label: 'Bar  ( | )' },
  { value: '\n', label: 'New line' },
  { value: '', label: 'Nothing' },
  { value: 'custom', label: 'Custom…' },
];

/** One sample record: its value now and after the change. */
function ExampleRow({ example, backupLabel }: { example: FieldTypeExample; backupLabel: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: 4, columnGap: 8, padding: '8px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      <Typography.Text strong style={{ gridColumn: '1 / -1' }}>{example.name || 'Unnamed'}</Typography.Text>
      <Typography.Text type="secondary">Now</Typography.Text>
      <div>{example.before.map((b, i) => <Tag key={i} style={{ marginBottom: 4 }}>{b}</Tag>)}</div>
      <Typography.Text type="secondary">After</Typography.Text>
      <div>
        {example.after === null ? (
          <Typography.Text type="warning">Moves to &quot;{backupLabel}&quot;</Typography.Text>
        ) : Array.isArray(example.after) ? (
          example.after.map((a, i) => <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{a}</Tag>)
        ) : (
          // pre-wrap keeps the separator visible, new lines included
          <Typography.Text code style={{ whiteSpace: 'pre-wrap' }}>{example.after}</Typography.Text>
        )}
        {example.kept && example.after !== null && <Typography.Text type="secondary"> (original also kept)</Typography.Text>}
      </div>
    </div>
  );
}

function ChangeTypeModal({ objectType, field, initialType, onClose }: { objectType: ObjectType; field: FieldDefinitionDto | null; initialType?: FieldType; onClose: () => void }) {
  const { message, modal } = App.useApp();
  const [type, setType] = useState<FieldType | undefined>(initialType);
  // undefined: the type's default (a space for text, a new line for long text)
  const [separatorChoice, setSeparatorChoice] = useState<string>();
  const [custom, setCustom] = useState('');
  const askSeparator = !!field && MULTI_VALUE_TYPES.has(field.type) && (type === 'TEXT' || type === 'TEXTAREA');
  const choice = separatorChoice ?? (type === 'TEXTAREA' ? '\n' : ' ');
  const separator = askSeparator ? (choice === 'custom' ? custom : choice) : undefined;
  // typing a custom separator previews once the typing stops (every preview reads all records)
  const [debounced, setDebounced] = useState(separator);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(separator), 300);
    return () => clearTimeout(t);
  }, [separator]);
  const change = useChangeFieldType();
  const preview = useFieldTypePreview(field?.id, type, debounced, change.isIdle || change.isError);
  const noun = RECORD_NOUN[objectType];
  const p = preview.data;
  const kept = p ? p.partial + p.unconvertible : 0;

  const apply = async () => {
    if (!field || !type) return;
    try {
      const r: ChangeFieldTypeResult = await change.mutateAsync({ id: field.id, type, separator });
      onClose();
      if (!r.backupField) message.success(`"${field.label}" is now ${TYPE_LABELS[type].toLowerCase()}; ${plural(r.converted, 'value')} converted`);
      else
        modal.info({
          title: `"${field.label}" is now ${TYPE_LABELS[type].toLowerCase()}`,
          content: (
            <p>
              {plural(r.converted, 'value')} converted. {plural(r.partial + r.unconvertible, noun)} kept {r.partial + r.unconvertible === 1 ? 'its' : 'their'} original value in the new field{' '}
              <b>{r.backupField.label}</b> (under More information), so nothing was lost.
            </p>
          ),
        });
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Modal
      title={`Change type: ${field?.label ?? ''}`}
      open={!!field}
      onCancel={onClose}
      onOk={() => void apply()}
      okText="Change type"
      okButtonProps={{ disabled: !type || !p || preview.isPlaceholderData || debounced !== separator }}
      confirmLoading={change.isPending}
      destroyOnHidden
      width={560}
    >
      {field && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            From <b>{TYPE_LABELS[field.type]}</b> to
          </Typography.Text>
          <Select<FieldType>
            autoFocus
            style={{ width: '100%' }}
            placeholder="New type"
            value={type}
            onChange={setType}
            options={FIELD_TYPES.filter((t) => t !== field.type).map((t) => ({ value: t, label: TYPE_LABELS[t] }))}
          />
          {type && <Alert type="info" showIcon message={askSeparator ? CONVERSION_NOTES[type] : CONVERSION_NOTES[type].replace(', joined by the separator below', '')} />}
          {askSeparator && (
            <div>
              <Typography.Text>Put between values</Typography.Text>
              <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                <Select style={{ width: choice === 'custom' ? '50%' : '100%' }} value={choice} onChange={setSeparatorChoice} options={SEPARATORS} />
                {choice === 'custom' && <Input autoFocus placeholder="e.g.  + " maxLength={10} value={custom} onChange={(e) => setCustom(e.target.value)} style={{ width: '50%' }} />}
              </Space.Compact>
            </div>
          )}
          {type && preview.isLoading && <Typography.Text type="secondary">Checking the stored values…</Typography.Text>}
          {preview.isError && <Alert type="error" showIcon message={errorMessage(preview.error)} />}
          {p && (
            <div>
              {p.total === 0 ? (
                <Typography.Text>No {noun} has a value in this field, so nothing needs converting.</Typography.Text>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>{plural(p.converted, 'value')} {p.converted === 1 ? 'converts as it is' : 'convert as they are'}</li>
                  {p.partial > 0 && <li>{plural(p.partial, 'value')} {p.partial === 1 ? 'converts in part and also keeps its' : 'convert in part and also keep their'} original</li>}
                  {p.unconvertible > 0 && <li>{plural(p.unconvertible, 'value')} {p.unconvertible === 1 ? 'does' : 'do'} not fit a {TYPE_LABELS[type!].toLowerCase()} field and {p.unconvertible === 1 ? 'moves' : 'move'} to the backup field</li>}
                  {p.newOptions > 0 && <li>{plural(p.newOptions, 'new option')} will be created</li>}
                </ul>
              )}
              {p.examples.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Typography.Text type="secondary">Preview of {plural(p.examples.length, noun)}</Typography.Text>
                  {p.examples.map((e) => <ExampleRow key={e.id} example={e} backupLabel={`${field.label} (original)`} />)}
                </div>
              )}
              {kept > 0 && (
                <Alert
                  style={{ marginTop: 12 }}
                  type="warning"
                  showIcon
                  message={<>Nothing is lost: original values are copied to a new field &quot;{field.label} (original)&quot; with the old type.</>}
                  description={p.samples.length ? <>For example: {p.samples.map((s, i) => <Tag key={i} style={{ marginTop: 4 }}>{s}</Tag>)}</> : undefined}
                />
              )}
              <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                Saved filters and segments that use this field may need their conditions updated.
              </Typography.Paragraph>
            </div>
          )}
        </Space>
      )}
    </Modal>
  );
}

/** "Work email" → "Work": the source label minus the channel word, as the default list label. */
const channelLabel = (label: string, into: 'emails' | 'phones' | 'links') =>
  label
    .replace(into === 'emails' ? /\s*e-?mail(\s*address)?\s*/gi : into === 'phones' ? /\s*(phone|tel(ephone)?)(\s*number)?\s*/gi : /\s*(url|link|profile)\s*/gi, ' ')
    .trim();

interface MergeFormValues {
  sourceId: string;
  into: string; // 'emails' | 'phones' | a field id
  label?: string;
}

function MergeFieldModal({ objectType, fields, source, onClose }: { objectType: ObjectType; fields: FieldDefinitionDto[]; source: FieldDefinitionDto | 'pick' | null; onClose: () => void }) {
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<MergeFormValues>();
  const merge = useMergeField();
  const orgFields = useFields('ORGANIZATION');
  const sourceId = Form.useWatch('sourceId', form);
  const into = Form.useWatch('into', form);
  const usage = useFieldUsage(sourceId);
  const from = fields.find((f) => f.id === sourceId);
  const toOrg = typeof into === 'string' && into.startsWith('org:');
  const target = toOrg ? orgFields.data?.find((f) => `org:${f.id}` === into) : fields.find((f) => f.id === into);
  const needsLabel = into === 'emails' || into === 'phones' || target?.type === 'LINKS' || (into === 'org:new' && from?.type === 'LINKS');
  const noun = RECORD_NOUN[objectType];

  const targets = fields.filter((f) => f.id !== sourceId && (!f.isSystem || MERGEABLE_SYSTEM_KEYS[objectType].includes(f.key)));
  const options = [
    ...(objectType === 'CONTACT'
      ? [{ label: 'Contact lists', options: [{ value: 'emails', label: 'Emails (with label)' }, { value: 'phones', label: 'Phones (with label)' }] }]
      : []),
    { label: 'Fields', options: targets.map((f) => ({ value: f.id, label: `${f.label} · ${TYPE_LABELS[f.type]}` })) },
    ...(objectType === 'CONTACT'
      ? [
          {
            label: "Contact's organization",
            options: [
              ...(from && !orgFields.data?.some((f) => f.key === from.key) ? [{ value: 'org:new', label: `New organization field "${from.label}"` }] : []),
              ...(orgFields.data ?? [])
                .filter((f) => !f.isSystem || (MERGEABLE_SYSTEM_KEYS.ORGANIZATION.includes(f.key) && f.key !== 'created_at'))
                .map((f) => ({ value: `org:${f.id}`, label: `Organization · ${f.label} · ${TYPE_LABELS[f.type]}` })),
            ],
          },
        ]
      : []),
  ];

  const explain = (): ReactNode => {
    if (!from || !into) return null;
    const src = <b>{from.label}</b>;
    if (into === 'emails')
      return <>Each value is added to the contact&apos;s Emails list with the label below. Several addresses separated by commas or semicolons become separate entries. Values that are not email addresses, or that another contact already has, stay in {src}.</>;
    if (into === 'phones')
      return <>Each value is added to the contact&apos;s Phones list with the label below. Several numbers separated by commas or semicolons become separate entries. Contacts without a phone get it as their primary number.</>;
    const orgNote = toOrg ? <> Each contact&apos;s value goes to its organization; contacts without an organization keep theirs. Several contacts of one organization add up the same way.</> : null;
    if (into === 'org:new') return <>A new organization field <b>{from.label}</b> is created and each contact&apos;s value is copied to its organization.{orgNote}</>;
    if (!target) return null;
    const dest = <b>{target.label}</b>;
    return <>{explainTarget(target, dest, src)}{orgNote}</>;
  };

  const explainTarget = (target: FieldDefinitionDto, dest: ReactNode, src: ReactNode): ReactNode => {
    if (target.type === 'LINKS') return <>Values are added to the {dest} links with the label below. Several URLs separated by commas or spaces become separate links; links already there are skipped.</>;
    if (target.type === 'LIST') return <>Values are appended to the {dest} list; values already there are skipped. Short text is split on commas.</>;
    if (target.type === 'MULTISELECT') return <>Values are appended to the {dest} list. Values that are not options yet are added as new options. Short text is split on commas.</>;
    if (target.isSystem && target.key === 'domain') return <>Values fill {dest} where it is empty (as a bare domain like example.com). Where {dest} already has a different value, or another organization has that domain, it stays in {src}.</>;
    if (target.isSystem && target.key === 'currency') return <>Values fill {dest} where it is empty or the same. Where {dest} already has a different value, it stays in {src}.</>;
    if (target.isSystem && target.key === 'created_at') return <>Each date replaces the record&apos;s {dest} date. Values that are not dates stay in {src}.</>;
    if (target.isSystem && target.type === 'BOOLEAN') return <>Yes values turn {dest} on (it starts as no). Where {dest} is already yes and the value is no, it stays in {src}.</>;
    if (target.type === 'TEXT') return <>Values are appended after the existing {dest} text, separated by a comma.</>;
    if (target.type === 'TEXTAREA') return <>Values are appended after the existing {dest} text, on a new line.</>;
    return <>Values fill {dest} where it is empty. Where {dest} already has a different value, or the value does not fit a {TYPE_LABELS[target.type].toLowerCase()} field, it stays in {src}.</>;
  };

  const onFinish = async (v: MergeFormValues) => {
    try {
      const input: MergeFieldInput =
        v.into === 'emails' || v.into === 'phones'
          ? { into: v.into, label: v.label }
          : v.into === 'org:new'
            ? { into: 'organization', label: v.label }
            : v.into.startsWith('org:')
              ? { into: 'organization', targetFieldId: v.into.slice(4), label: v.label }
              : { into: 'field', targetFieldId: v.into, label: v.label };
      const r: MergeFieldResult = await merge.mutateAsync({ id: v.sourceId, ...input });
      const left = r.left.invalid + r.left.taken + r.left.conflict + r.left.noOrganization;
      if (!left) {
        message.success(`Merged ${plural(r.moved, noun)}${r.deleted ? `; "${from?.label}" was removed` : ''}`);
      } else {
        modal.info({
          title: `Merged ${plural(r.moved, noun)}`,
          content: (
            <>
              <p>{plural(left, noun)} kept their value in &quot;{from?.label}&quot;, so the field stays:</p>
              <ul>
                {r.left.invalid > 0 && <li>{plural(r.left.invalid, 'value')} did not fit the destination</li>}
                {r.left.taken > 0 && <li>{plural(r.left.taken, 'address', 'addresses')} already belong to another contact</li>}
                {r.left.conflict > 0 && <li>{plural(r.left.conflict, noun)} already had a different value</li>}
                {r.left.noOrganization > 0 && <li>{plural(r.left.noOrganization, noun)} have no organization</li>}
              </ul>
            </>
          ),
        });
      }
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Modal
      title="Merge fields"
      open={!!source}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Merge"
      confirmLoading={merge.isPending}
      destroyOnHidden
      width={560}
    >
      <Form<MergeFormValues>
        form={form}
        preserve={false}
        layout="vertical"
        requiredMark={false}
        initialValues={{ sourceId: source && source !== 'pick' ? source.id : undefined }}
        onFinish={(v) => void onFinish(v)}
        onValuesChange={(changed, all) => {
          if (changed.sourceId && changed.sourceId === all.into) form.setFieldValue('into', undefined);
          const f = fields.find((x) => x.id === all.sourceId);
          const t = all.into?.startsWith('org:') ? orgFields.data?.find((x) => `org:${x.id}` === all.into) : fields.find((x) => x.id === all.into);
          const kind = all.into === 'emails' || all.into === 'phones' ? all.into : t?.type === 'LINKS' ? 'links' : null;
          if ((changed.sourceId || changed.into) && f && kind) form.setFieldValue('label', channelLabel(f.label, kind));
        }}
      >
        <Form.Item
          name="sourceId"
          label="Merge this field"
          rules={[{ required: true, message: 'Pick the field to merge away' }]}
          extra={usage.data ? `${plural(usage.data.count, noun)} ${usage.data.count === 1 ? 'has' : 'have'} a value. The field is removed once every value has moved.` : undefined}
        >
          <Select showSearch optionFilterProp="label" placeholder="Field to merge away" options={fields.filter((f) => !f.isSystem).map((f) => ({ value: f.id, label: `${f.label} · ${TYPE_LABELS[f.type]}` }))} />
        </Form.Item>
        <Form.Item name="into" label="Into" rules={[{ required: true, message: 'Pick where the values go' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Destination" options={options} />
        </Form.Item>
        {/* hidden rather than unmounted, so the label prefilled on picking a list is kept */}
        <Form.Item name="label" label="Label" hidden={!needsLabel} extra="Shown next to each moved value, e.g. Work, Mobile, LinkedIn. Leave empty for no label.">
          <Input placeholder={into === 'emails' ? 'e.g. Work' : into === 'phones' ? 'e.g. Mobile' : 'e.g. LinkedIn'} maxLength={60} />
        </Form.Item>
        {explain() && <Alert type="info" showIcon message={explain()} />}
      </Form>
    </Modal>
  );
}
